import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Divider, Typography } from "@mui/material";

import { getEntityProfileRoute } from "../../../../../../routers/routeUtils";

import { entityKeys } from "../../../../../Entity/api/queryKeys";
import { useCreateEntityBranches } from "../../../../../Entity/api/mutations/useCreateEntityBranches";
import { useGetEntityLocationsByCountry } from "../../../../../Entity/api/queries/useGetEntityLocationsByCountry";

import { addEntityLocationsSchema } from "../../../../schemas/editEntityProfile";
import { AddEntityLocationsFormData } from "../../../../types/editEntityProfile-type";

import { EntityLocations } from "../../../EntityProfile/components/EntityLocations";

import AddLocation from "./AddLocation";
import BranchLocation from "./BranchLocation";

const LocationsTab = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEdit" });
  const navigate = useNavigate();

  const { entityId } = useParams();
  const { data: entityLocations, isLoading: isLoadingLocations } = useGetEntityLocationsByCountry({
    entityId: entityId!,
  });

  const queryClient = useQueryClient();
  const methods = useForm<AddEntityLocationsFormData>({
    resolver: zodResolver(addEntityLocationsSchema),
    defaultValues: {
      locations: [],
    },
    mode: "onChange",
  });

  const handleAddLocation = () => {
    const newLocation = {
      country: undefined as unknown as number, // Will trigger the required validation
      branches: [
        {
          branchName: "",
          branchAddress: "",
          branchGoogleMapsAddress: "",
          isHeadquarters: false,
          workingHours: {},
          sports: [],
          facilities: [],
        },
      ],
    };
    methods.setValue("locations", [...locations, newLocation]);
  };

  const { mutate: createBranches, isPending } = useCreateEntityBranches();

  const onSubmit = (data: AddEntityLocationsFormData) => {
    createBranches(
      { data, entityId: Number(entityId!) },
      {
        onSuccess: () => {
          setTimeout(() => {
            methods.reset();
            navigate(getEntityProfileRoute(entityId!));
            queryClient.invalidateQueries({ queryKey: entityKeys.locationsByCountry });
          }, 2000);
        },
        onError: (error: unknown) => {
          console.error("Error creating branches:", error);
        },
      }
    );
  };

  const onError = (errors: any) => {
    console.error("onError | Form Errors:", errors);
  };

  const handleCancel = () => {
    methods.reset();
  };

  const locations = methods.watch("locations");

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("locations")}</Typography>
      <EntityLocations canModify={true} />
      <FormProvider {...methods}>
        <Box component="form" onSubmit={methods.handleSubmit(onSubmit, onError)} sx={styles.formContainer}>
          {locations?.length > 0 &&
            locations?.map((location, index) => <BranchLocation key={location.country} index={index} />)}

          <AddLocation singular={locations.length === 0} onAdd={handleAddLocation} />

          <Divider orientation="horizontal" sx={styles.divider} />

          <Box sx={styles.actionsContainer}>
            <Button variant="outlined-sm" type="button" onClick={handleCancel}>
              {t("cancel")}
            </Button>
            <Button variant="primary-sm" type="submit" disabled={isPending || methods.formState.isSubmitting}>
              {t("save")}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default LocationsTab;

const styles = {
  container: {
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    padding: "24px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  title: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "24px",
  },
  divider: {
    mx: "-16px",
  },
  actionsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "16px",
  },
};
